import React, { useLayoutEffect, useRef, useState, useEffect } from "react"
import { FormattedMessage } from "react-intl"
import { WingsButton } from "../buttons"
import { convertHtmlToWingsUiComponents } from "../../../../utils"
import "./HTMLViewMore.css"

/**
 * Expandable/collapsible text section that allows rich text
 * @param {string} html
 * @param {number} lines - max lines of text to show when collapsed
 * @param {string} className - for custom styling
 * @param {string} backgroundColor - background color of content container, defaults to white
 * @returns {JSX}
 */
const HTMLViewMore = ({ html, lines=5, className="", backgroundColor="var(--w-color-white)" }) => {
  const [canExpand, setCanExpand] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const contentRef = useRef(null)
  // if defined, the pre-collapse distance of the content from the bottom of window 
  const contentScrollPositionRef = useRef(null)

  // when the size of the content box changes, we have to re-evaluate whether a
  // view more/less toggle is necessary
  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    setCanExpand(content.scrollHeight > content.clientHeight)

    const observer = new ResizeObserver(() => {
      setCanExpand(isExpanded || content.scrollHeight > content.clientHeight)
    })

    observer.observe(content)

    return () => observer.disconnect()
  }, [])

  // After collapsing the content section, scroll to keep the bottom of the
  // content section in the same position on screen
  useLayoutEffect(() => {
    if (!contentScrollPositionRef.current || isExpanded) {
      return
    }

    const content = contentRef.current
    if (content) {
      const contentRect = content.getBoundingClientRect()
      const currentDistanceFromBottom = window.innerHeight - contentRect.bottom
      const scrollAmount = contentScrollPositionRef.current - currentDistanceFromBottom
      window.scrollBy({ top: scrollAmount, behavior: "instant" })
    }

    contentScrollPositionRef.current = null
  }, [isExpanded])

  const handleToggle = () => {
    if (isExpanded) {
      const contentRect = contentRef.current.getBoundingClientRect()
      const distanceFromBottom = window.innerHeight - contentRect.bottom
      contentScrollPositionRef.current = distanceFromBottom
    }
    setIsExpanded(!isExpanded)
  }

  const clampingStyles = {
    WebkitLineClamp: isExpanded ? "none" : lines,
    lineClamp: isExpanded ? "none" : lines,
  }

  const viewMoreButtonStyles = {
    "--button-background-color": backgroundColor,
    // background of left padding is used to fade out preceding text
    paddingLeft: "var(--w-spacing-3)",
    background: `
      linear-gradient(
        to right,
        transparent,
        var(--button-background-color) var(--w-spacing-3)
      )
    `,
  }

  return (
    <div className={`html-view-more-outer-container w-full w-flex w-flex-column-reverse w-items-start ${className}`}>
      {(canExpand || isExpanded) && (
        <WingsButton
          className={`html-view-more-toggle-button ${isExpanded ? "view-less-toggle" : "view-more-toggle"}`}
          variant="tertiary"
          onClick={handleToggle}
          inline={true}
          style={isExpanded ? {} : viewMoreButtonStyles}
          size="small"
        >
          {isExpanded ? <FormattedMessage id="view-less" defaultMessage="View Less" /> : <FormattedMessage id="view-more" defaultMessage="View More" />}
        </WingsButton>
      )}
      <div
        className="html-view-more-container w-full"
        style={clampingStyles}
        ref={contentRef}
        inert={(canExpand && !isExpanded) ? "true" : undefined}
      >
        {convertHtmlToWingsUiComponents(html)}
      </div>
    </div>
  )
}

export default HTMLViewMore
